import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getData, postData } from '../../services/api';
import RegistrationPathSelector from '../../components/auth/RegistrationPathSelector';

export default function TenantUserRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledCode = searchParams.get('code') || searchParams.get('tenant') || '';

  const [tenantInfo, setTenantInfo] = useState(null);
  const [tenantError, setTenantError] = useState('');
  const [orgCode, setOrgCode] = useState(prefilledCode);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
  });
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpMaskedPhone, setOtpMaskedPhone] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpVerifyLoading, setOtpVerifyLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleRegistrationTenantResolved = useCallback((tenant) => {
    if (!tenant?.slug) return;
    setOrgCode((current) => current.trim() || String(tenant.slug));
    setTenantInfo((current) => current || tenant);
    setTenantError('');
  }, []);

  useEffect(() => {
    if (!orgCode.trim() || orgCode.trim().length < 4) {
      setTenantInfo(null);
      setTenantError('');
      return undefined;
    }
    const timer = setTimeout(() => {
      getData(`/auth/resolve-tenant?slug=${encodeURIComponent(orgCode.trim())}`)
        .then((res) => {
          setTenantInfo(res?.data || res || null);
          setTenantError('');
        })
        .catch(() => {
          setTenantInfo(null);
          setTenantError('Organization not found. Check the code and try again.');
        });
    }, 500);
    return () => clearTimeout(timer);
  }, [orgCode]);

  useEffect(() => {
    if (!otpCooldown) return undefined;
    const timer = setInterval(() => {
      setOtpCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpCooldown]);

  useEffect(() => {
    setOtp('');
    setOtpSent(false);
    setOtpVerified(false);
    setOtpMaskedPhone('');
    setSuccessMessage('');
    setError('');
  }, [orgCode, form.phone]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSendOtp = async () => {
    setError('');
    setSuccessMessage('');
    if (!orgCode.trim()) {
      setError('Please enter your organization code.');
      return;
    }
    if (tenantError) {
      setError('Organization not found. Please check the code.');
      return;
    }
    if (!form.phone.trim()) {
      setError('Please enter your phone number.');
      return;
    }
    setOtpLoading(true);
    try {
      const res = await postData('/auth/send-registration-otp', {
        organizationCode: orgCode.trim(),
        phone: form.phone,
      });
      const payload = res?.data || res || {};
      setOtpSent(true);
      setOtpVerified(false);
      setOtpMaskedPhone(payload.phone || '');
      setOtpCooldown(payload.resendCooldownSeconds || 60);
      setSuccessMessage('OTP sent to your phone number.');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to send OTP.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    setSuccessMessage('');
    if (otp.trim().length !== 6) {
      setError('Please enter the 6-digit OTP.');
      return;
    }
    setOtpVerifyLoading(true);
    try {
      await postData('/auth/verify-registration-otp', {
        organizationCode: orgCode.trim(),
        phone: form.phone,
        otp: otp.trim(),
      });
      setOtpVerified(true);
      setSuccessMessage('OTP verified. You can complete registration now.');
    } catch (err) {
      setOtpVerified(false);
      setError(err.response?.data?.message || err.message || 'Failed to verify OTP.');
    } finally {
      setOtpVerifyLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    if (!orgCode.trim()) {
      setError('Please enter your organization code.');
      return;
    }
    if (tenantError) {
      setError('Organization not found. Please check the code.');
      return;
    }
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !form.password.trim()) {
      setError('First name, last name, email, and password are required.');
      return;
    }
    if (form.phone.trim() && !otpVerified) {
      setError('Please verify your phone number before creating the account.');
      return;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);
    try {
      await postData('/auth/self-register', {
        ...form,
        organizationCode: orgCode.trim(),
      });
      navigate(`/login?tenant=${encodeURIComponent(orgCode.trim())}`, {
        replace: true,
        state: {
          registrationMessage: `Account created for ${tenantInfo?.name || 'your organization'}. You can log in after admin approval.`,
        },
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-glass" style={{ maxWidth: 580 }}>
      <h4 className="text-center fw-bold mb-1">Create Account</h4>
      <p className="text-center text-muted mb-3" style={{ fontSize: 13 }}>
        Create your learner account.
      </p>

      <RegistrationPathSelector
        tenantSlug={prefilledCode}
        onTenantResolved={handleRegistrationTenantResolved}
      />

      {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{error}</div>}
      {successMessage && <div className="alert alert-success py-2 mb-3" style={{ fontSize: 13 }}>{successMessage}</div>}

      <form onSubmit={handleSubmit}>
        {tenantError && <div className="small text-danger mb-3">{tenantError}</div>}

        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">First Name *</label>
            <input className="form-control" value={form.firstName} onChange={(e) => setField('firstName', e.target.value)} required />
          </div>
          <div className="col-md-6">
            <label className="form-label">Last Name *</label>
            <input className="form-control" value={form.lastName} onChange={(e) => setField('lastName', e.target.value)} required />
          </div>
          <div className="col-12">
            <label className="form-label">Email *</label>
            <input type="email" className="form-control" value={form.email} onChange={(e) => setField('email', e.target.value)} required />
          </div>
          <div className="col-md-12">
            <label className="form-label">Phone Number <span className="text-muted">(optional)</span></label>
            <div className="row g-2 align-items-stretch">
              <div className="col-md-7">
                <input type="tel" className="form-control h-100" value={form.phone} onChange={(e) => setField('phone', e.target.value)} />
              </div>
              <div className="col-md-5">
                <button
                  type="button"
                  className="btn w-100 h-100"
                  style={{
                    minHeight: 44,
                    background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
                    border: '1px solid rgba(96,165,250,0.55)',
                    color: '#fff',
                    boxShadow: '0 10px 24px rgba(37,99,235,0.32)',
                    fontWeight: 700,
                  }}
                  onClick={handleSendOtp}
                  disabled={!orgCode.trim() || !!tenantError || !form.phone.trim() || otpLoading || otpCooldown > 0}
                >
                  {otpLoading ? 'Sending...' : otpCooldown > 0 ? `Retry in ${otpCooldown}s` : otpSent ? 'Resend OTP' : 'Send OTP'}
                </button>
              </div>
            </div>
            {otpSent && otpMaskedPhone && (
              <div className="small text-muted mt-1">OTP sent to {otpMaskedPhone}</div>
            )}
          </div>

          {otpSent && (
            <>
              <div className="col-md-12">
                <label className="form-label">Verify OTP *</label>
                <div className="row g-2 align-items-stretch">
                  <div className="col-md-7">
                    <input
                      className="form-control h-100"
                      inputMode="numeric"
                      maxLength={6}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit OTP"
                    />
                  </div>
                  <div className="col-md-5">
                    <button
                      type="button"
                      className={`btn w-100 h-100 ${otpVerified ? 'btn-success' : ''}`}
                      style={otpVerified ? { minHeight: 44 } : {
                        minHeight: 44,
                        background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
                        border: '1px solid rgba(96,165,250,0.55)',
                        color: '#fff',
                        boxShadow: '0 10px 24px rgba(37,99,235,0.32)',
                        fontWeight: 700,
                      }}
                      onClick={handleVerifyOtp}
                      disabled={otpVerifyLoading || otpVerified}
                    >
                      {otpVerified ? 'OTP Verified' : otpVerifyLoading ? 'Verifying...' : 'Verify OTP'}
                    </button>
                  </div>
                </div>
                {otpVerified && (
                  <div className="small mt-2" style={{ color: '#86efac' }}>
                    Phone number verified successfully.
                  </div>
                )}
              </div>
            </>
          )}

          <div className="col-12">
            <label className="form-label">Password *</label>
            <div className="input-group">
              <input
                type={showPass ? 'text' : 'password'}
                className="form-control"
                value={form.password}
                onChange={(e) => setField('password', e.target.value)}
                minLength={6}
                required
              />
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowPass((prev) => !prev)}>
                <i className={`bi ${showPass ? 'bi-eye-slash' : 'bi-eye'}`}></i>
              </button>
            </div>
          </div>
        </div>

        <button type="submit" className="btn btn-primary w-100 mt-4" disabled={loading}>
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>
      </form>

      <div className="text-center mt-3" style={{ fontSize: 13 }}>
        Already registered? <Link to={orgCode ? `/login?tenant=${encodeURIComponent(orgCode)}` : '/login'}>Sign in</Link>
      </div>
    </div>
  );
}
